import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import {
  X,
  Send,
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  Eye,
  EyeOff,
  Mic,
  MicOff,
  Loader2
} from 'lucide-react'
import { getFreshAccessToken } from '../../../lib/api/api'
import { isBlockCode } from '../../../shared/utils/markdown'
import { recordingFileName, transcriptionErrorText } from '../utils/dictation'
import toast from 'react-hot-toast'
import {
  insertMarkdown,
  insertLinePrefix,
  insertCodeBlock,
  insertHorizontalRule,
  applyMarkdownResult
} from '../utils/markdownHelpers'

interface MarkdownEditorModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (content: string) => void
  initialValue?: string
  isLoading?: boolean
}

interface ToolbarButton {
  icon: React.ReactNode
  title: string
  action: (textarea: HTMLTextAreaElement, value: string, setValue: (v: string) => void) => void
  separator?: boolean
}

export default function MarkdownEditorModal({
  isOpen,
  onClose,
  onSubmit,
  initialValue = '',
  isLoading = false
}: MarkdownEditorModalProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState(initialValue)
  const [showPreview, setShowPreview] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const mediaStreamRef = useRef<MediaStream | null>(null)

  const hasMicSupport = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia

  // Update value when initialValue changes
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current && !showPreview) {
      textareaRef.current.focus()
    }
  }, [isOpen, showPreview])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isOpen && value.trim()) {
        handleSubmit()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // handleSubmit is recreated on every render: re-subscribing on it would
    // change nothing (value is already a dependency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value, onClose])

  // Stop recording when modal closes
  useEffect(() => {
    if (!isOpen && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      mediaStreamRef.current?.getTracks().forEach(t => t.stop())
      mediaRecorderRef.current = null
      mediaStreamRef.current = null
      setIsRecording(false)
    }
  }, [isOpen])

  const transcribeAudio = async (blob: Blob, fileName: string) => {
    setIsTranscribing(true)
    try {
      const token = await getFreshAccessToken()
      if (!token) return
      const formData = new FormData()
      formData.append('file', blob, fileName)
      const resp = await fetch('/api/v1/chat-stream/transcribe', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })
      if (resp.ok) {
        const data = await resp.json()
        if (data.text) {
          setValue(prev => prev ? prev + ' ' + data.text : data.text)
        }
      } else {
        toast.error(await transcriptionErrorText(resp, t('chat.input.transcriptionFailed')))
      }
    } catch (err) {
      console.error('Transcription error:', err)
      toast.error(t('chat.input.transcriptionFailed'))
    } finally {
      setIsTranscribing(false)
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : ''
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      audioChunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(audioChunksRef.current, { type })
        transcribeAudio(blob, recordingFileName(type))
      }
      recorder.start()
      mediaRecorderRef.current = recorder
      setIsRecording(true)
    } catch (err) {
      console.error('Microphone access denied:', err)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    mediaStreamRef.current?.getTracks().forEach(t => t.stop())
    mediaRecorderRef.current = null
    mediaStreamRef.current = null
    setIsRecording(false)
  }

  if (!isOpen) return null

  const handleSubmit = () => {
    if (value.trim() && !isLoading) {
      onSubmit(value)
      setValue('')
    }
  }

  const toolbarButtons: ToolbarButton[] = [
    {
      icon: <Bold size={18} />,
      title: t('chat.composeModal.toolbar.bold'),
      action: (textarea, _, setVal) => {
        const result = insertMarkdown(textarea, '**', '**', t('chat.composeModal.toolbar.boldPlaceholder'))
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Italic size={18} />,
      title: t('chat.composeModal.toolbar.italic'),
      action: (textarea, _, setVal) => {
        const result = insertMarkdown(textarea, '*', '*', t('chat.composeModal.toolbar.italicPlaceholder'))
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Strikethrough size={18} />,
      title: t('chat.composeModal.toolbar.strikethrough'),
      action: (textarea, _, setVal) => {
        const result = insertMarkdown(textarea, '~~', '~~', t('chat.composeModal.toolbar.strikethroughPlaceholder'))
        applyMarkdownResult(textarea, result, setVal)
      },
      separator: true
    },
    {
      icon: <Heading1 size={18} />,
      title: t('chat.toolbar.heading1'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '# ')
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Heading2 size={18} />,
      title: t('chat.toolbar.heading2'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '## ')
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Heading3 size={18} />,
      title: t('chat.composeModal.toolbar.heading3'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '### ')
        applyMarkdownResult(textarea, result, setVal)
      },
      separator: true
    },
    {
      icon: <List size={18} />,
      title: t('chat.composeModal.toolbar.bulletList'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '- ')
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <ListOrdered size={18} />,
      title: t('chat.composeModal.toolbar.numberedList'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '1. ')
        applyMarkdownResult(textarea, result, setVal)
      },
      separator: true
    },
    {
      icon: <Quote size={18} />,
      title: t('chat.toolbar.quote'),
      action: (textarea, _, setVal) => {
        const result = insertLinePrefix(textarea, '> ')
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Code size={18} />,
      title: t('chat.composeModal.toolbar.code'),
      action: (textarea, _, setVal) => {
        const result = insertCodeBlock(textarea)
        applyMarkdownResult(textarea, result, setVal)
      }
    },
    {
      icon: <Minus size={18} />,
      title: t('chat.composeModal.toolbar.hr'),
      action: (textarea, _, setVal) => {
        const result = insertHorizontalRule(textarea)
        applyMarkdownResult(textarea, result, setVal)
      }
    }
  ]

  const handleToolbarClick = (button: ToolbarButton) => {
    if (textareaRef.current) {
      button.action(textareaRef.current, value, setValue)
    }
  }

  // Handle keyboard shortcuts in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') {
        e.preventDefault()
        handleToolbarClick(toolbarButtons[0]) // Bold
      } else if (e.key === 'i') {
        e.preventDefault()
        handleToolbarClick(toolbarButtons[1]) // Italic
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full mx-4">
          {/* Header */}
          <div className="bg-primary-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-white">
                {t('chat.composeModal.title')}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
                title={t('chat.composeModal.closeTitle')}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-1">
              {toolbarButtons.map((button, index) => (
                <React.Fragment key={index}>
                  <button
                    type="button"
                    onClick={() => handleToolbarClick(button)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    title={button.title}
                    disabled={showPreview}
                  >
                    {button.icon}
                  </button>
                  {button.separator && (
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
                  )}
                </React.Fragment>
              ))}

              {/* Preview toggle */}
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={`flex items-center gap-2 px-3 py-2 rounded transition-colors ${
                  showPreview
                    ? 'bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
                title={showPreview ? t('common.edit') : t('chat.composeModal.preview')}
              >
                {showPreview ? <EyeOff size={18} /> : <Eye size={18} />}
                <span className="text-sm hidden sm:inline">
                  {showPreview ? t('common.edit') : t('chat.composeModal.preview')}
                </span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-900 px-6 py-4">
            {showPreview ? (
              <div className="min-h-[40vh] max-h-[60vh] overflow-y-auto prose prose-sm max-w-none p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                {value.trim() ? (
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h1 className="text-2xl font-bold mt-4 mb-2 text-gray-900 dark:text-gray-100">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-xl font-semibold mt-3 mb-2 text-gray-800 dark:text-gray-200">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-medium mt-2 mb-1 text-gray-700 dark:text-gray-300">{children}</h3>,
                      p: ({ children }) => <p className="mb-2 text-gray-600 dark:text-gray-400 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="text-gray-600 dark:text-gray-400">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-gray-800 dark:text-gray-200">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      del: ({ children }) => <del className="line-through text-gray-500">{children}</del>,
                      blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400 my-2">{children}</blockquote>,
                      // react-markdown 9 has no `inline` prop: blocks are already
                      // wrapped in <pre>, so style <pre> and detect block code.
                      pre: ({ children }) => <pre className="bg-gray-800 text-white p-3 rounded my-2 overflow-x-auto">{children}</pre>,
                      code: ({ node, className, children, ...props }) => (
                        isBlockCode(className, children)
                          ? <code className={`text-sm font-mono ${className || ''}`} {...props}>{children}</code>
                          : <code className="bg-gray-200 dark:bg-gray-700 rounded px-1 py-0.5 text-sm font-mono" {...props}>{children}</code>
                      ),
                      hr: () => <hr className="my-4 border-gray-300 dark:border-gray-600" />
                    }}
                  >
                    {value}
                  </ReactMarkdown>
                ) : (
                  <p className="text-gray-400 italic">{t('chat.composeModal.noContent')}</p>
                )}
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('chat.composeModal.placeholder')}
                className="w-full min-h-[40vh] max-h-[60vh] p-4 border border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
                disabled={isLoading}
              />
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 hidden sm:block">
                {t('chat.composeModal.hint')}
              </p>
              <div className="flex gap-3 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                {hasMicSupport && (
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTranscribing}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isRecording
                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                        : isTranscribing
                          ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                    title={isRecording ? t('chat.composeModal.stopRecording') : isTranscribing ? t('chat.input.transcribing') : t('chat.composeModal.recordVoice')}
                  >
                    {isTranscribing ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : isRecording ? (
                      <MicOff size={18} />
                    ) : (
                      <Mic size={18} />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!value.trim() || isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {t('chat.composeModal.send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
